export const useProductsStore = defineStore('products', {
  state: () => ({
    products: null as PaginatedProduct | null,
    productOfferings: null as PaginatedProductOffering | null,
  }),
  getters: {
    getProducts: (state) => (state.products !== null ? state.products.items : []),
    getProductOfferings: (state) =>
      state.productOfferings !== null ? state.productOfferings.items : [],
  },
  actions: {
    setProducts(products: PaginatedProduct) {
      useLocalStorage().setItem('products', products);
      this.products = products;
    },
    setProductOfferings(productOfferings: PaginatedProductOffering) {
      useLocalStorage().setItem('productOfferings', productOfferings);
      this.productOfferings = productOfferings;
    },
    async createProduct(product: ProductCreateUpdate): Promise<Product | undefined> {
      try {
        const {data} = await apiPostProduct(product);
        pushDataToPaginatedState(this.products, data);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readProducts({
      offset,
      limit,
      sku_str,
      search,
      lifecycle_stage,
    }: {
      offset?: number;
      limit?: number;
      sku_str?: string;
      search?: string;
      lifecycle_stage?: string;
    } = {}): Promise<PaginatedProduct | undefined> {
      const localStorageProducts: PaginatedProduct | null = useLocalStorage().getItem('products');
      if (localStorageProducts) this.setProducts(localStorageProducts);
      try {
        const {data} = await apiGetProducts(limit, offset, sku_str, search, lifecycle_stage);
        this.setProducts(data);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readProductById(id: string | number): Promise<Product | undefined> {
      const localStorageProducts: PaginatedProduct | null = useLocalStorage().getItem('products');
      if (localStorageProducts) {
        const foundProduct = localStorageProducts.items.find((p) => p.id === id);
        if (foundProduct !== undefined) return foundProduct;
      }
      try {
        const {data} = await apiGetProduct(id);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async updateProduct(product: ProductCreateUpdate): Promise<Product | undefined> {
      try {
        const response = await apiPutProduct(product);
        const data: Product = response.data;
        // Find & update the target object.
        if (this.products !== null) {
          const index = this.products.items.findIndex((p) => p.id === data.id);
          if (index >= 0) {
            Object.assign(this.products.items[index], data);
            useLocalStorage().setItem('products', this.products);
          }
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async deleteProduct(id: string | number) {
      try {
        await apiDeleteProduct(id);
        // Find & remove the element.
        if (this.products !== null) {
          const index = this.products.items.findIndex((p) => p.id === id);
          if (index >= 0) {
            this.products.items.splice(index, 1);
            this.products.total--;
            useLocalStorage().setItem('products', this.products);
          }
        }
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    /**
     * Product Offering Actions
     */
    async createProductOffering(
      productOffering: ProductOfferingCreateUpdate,
    ): Promise<ProductOffering | undefined> {
      try {
        const {data} = await apiPostProductOffering(productOffering);
        // Push the object to state.
        if (this.products !== null) {
          const index = this.products.items.findIndex((p) => p.id === data.product_id);
          if (index >= 0) {
            this.products.items[index].offerings.push(data);
            useLocalStorage().setItem('products', this.products);
          }
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readProductOfferings({
      offset,
      limit,
    }: {
      offset?: number;
      limit?: number;
    } = {}): Promise<PaginatedProductOffering | undefined> {
      // Parse the local storage value & set the state.
      const localStorageProductOfferings: PaginatedProductOffering | null =
        useLocalStorage().getItem('productOfferings');
      if (localStorageProductOfferings) this.setProductOfferings(localStorageProductOfferings);
      try {
        const {data} = await apiGetProductOfferings(limit, offset);
        this.setProductOfferings(data);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readProductOfferingById(id: string | number): Promise<ProductOffering | undefined> {
      // Parse & return the local storage value.
      const localStorageProducts: PaginatedProduct | null = useLocalStorage().getItem('products');
      if (localStorageProducts) {
        for (const product of localStorageProducts.items) {
          const foundOffering = product.offerings.find((o) => o.id === id);
          if (foundOffering !== undefined) return foundOffering;
        }
      }
      try {
        const {data} = await apiGetProductOffering(id);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async updateProductOffering(
      productOffering: ProductOfferingCreateUpdate,
    ): Promise<ProductOffering | undefined> {
      try {
        const response = await apiPutProductOffering(productOffering);
        const data: ProductOffering = response.data;
        // Find & update the target object.
        if (this.products !== null) {
          // Find the product
          const pindex = this.products.items.findIndex((p) => p.id === data.product_id);
          if (pindex >= 0) {
            // Find the offering in the product
            const oindex = this.products.items[pindex].offerings.findIndex((o) => o.id === data.id);
            if (oindex >= 0) {
              Object.assign(this.products.items[pindex].offerings[oindex], data);
              useLocalStorage().setItem('products', this.products);
            }
          }
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async deleteProductOffering(id: string | number) {
      try {
        const response = await apiDeleteProductOffering(id);
        const data: ProductOffering = response.data;
        // Find & remove the element.
        if (this.products !== null) {
          // Find the product
          const pindex = this.products.items.findIndex((p) => p.id === data.product_id);
          if (pindex >= 0) {
            // Find the offering in the product
            const oindex = this.products.items[pindex].offerings.findIndex((o) => o.id === data.id);
            if (oindex >= 0) {
              this.products.items[pindex].offerings.splice(oindex, 1);
              useLocalStorage().setItem('products', this.products);
            }
          }
        }
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
  },
});
